// Матрица «блок × слово» по данным /getstream.
//
// Одна сессия несёт ровно одну utm-фразу (stat-back/src/query/dbAdd.js:51-63),
// поэтому ses/ch/ch_c по разным фразам можно складывать. Проценты — нельзя:
// ch_p и ch_p_c приходят строками вида "15%" и всегда пересчитываются
// формулой ch / ses. Внутри матрицы проценты хранятся долями 0..1,
// форматирование живёт на слое отображения.
//
// Токенизация та же, что в разрезе по слову: splitPhraseToWords из wordStat.js.
import { splitPhraseToWords } from './wordStat';

const num = v => Number(v) || 0;
const share = (part, total) => (total ? part / total : 0);

export function buildWordMatrix(sortedObjects) {
  const blocks = Object.keys(sortedObjects || {});
  // Object.create(null): utm_term приходит от посетителя сайта и может
  // содержать "__proto__" — обычный {} превратил бы такой ключ в
  // переустановку прототипа вместо обычного свойства.
  const acc = Object.create(null);
  // слово → Set фраз, для сегментов (клик по слову кладёт все его фразы)
  const pbw = Object.create(null);

  blocks.forEach((block, bi) => {
    const utm = (sortedObjects[block] && sortedObjects[block].utm) || {};
    for (const phrase in utm) {
      if (!Object.prototype.hasOwnProperty.call(utm, phrase)) continue;
      const m = utm[phrase] || {};
      for (const word of splitPhraseToWords(phrase)) {
        (pbw[word] || (pbw[word] = new Set())).add(phrase);
        if (!acc[word]) {
          acc[word] = { word: word, raw: blocks.map(() => ({ ses: 0, ch: 0, chC: 0 })) };
        }
        const cell = acc[word].raw[bi];
        cell.ses += num(m.ses);
        cell.ch += num(m.ch);
        cell.chC += num(m.ch_c);
      }
    }
  });

  const words = Object.keys(acc).map(key => {
    const rec = acc[key];
    // Знаменатель — сессии слова В ЭТОМ БЛОКЕ, ровно как в колонке «Разрез по
    // слову» (wordStat.aggregateWords). Обычно бэкенд дозаполняет каждую фразу
    // во все блоки (getStat.js:554-569), и знаменатель везде один и тот же.
    // Но если фраза в блоке всё-таки отсутствует, её сессии не должны попадать
    // в знаменатель этого блока: раньше сюда подставлялся ses первого блока,
    // процент занижался и на широком срезе схлопывался до нуля — матрица
    // показывала «—» там, где в отчёте по блокам стоит нормальное значение.
    let ses = 0;
    for (const cell of rec.raw) {
      if (cell.ses > ses) ses = cell.ses;
    }
    return {
      word: rec.word,
      // Для сортировки, порога «мин. сессий» и профиля слова нужна одна
      // величина на слово — берём наибольший охват по блокам.
      ses: ses,
      cells: rec.raw.map(cell => ({
        ch: cell.ch,
        chC: cell.chC,
        chP: share(cell.ch, cell.ses),
        chPc: share(cell.chC, cell.ses),
      })),
    };
  });

  // Порядок по умолчанию: сессии по убыванию, при равных — алфавит.
  words.sort((a, b) => (b.ses - a.ses) || a.word.localeCompare(b.word, 'ru'));
  const phrasesByWord = Object.create(null);
  for (const w in pbw) phrasesByWord[w] = [...pbw[w]];
  return { blocks: blocks, words: words, phrasesByWord: phrasesByWord };
}

// Столбец-сегмент. Одна сессия несёт одну фразу, поэтому фраза, пришедшая через два
// слова, считается один раз — дедуп по самой фразе заменяет дедуп по id сессии из
// прототипа. Проценты пересчитываются от суммы ses: это отток объединённой аудитории,
// а не среднее по словам с равным весом, как в прототипе.
export function buildSegmentColumn(sortedObjects, parts, convByPhrase) {
  const phrases = [...new Set((parts || []).map(p => p.phrase))];
  const blocks = Object.keys(sortedObjects || {});
  let ses = 0;
  const cells = blocks.map(b => {
    const utm = (sortedObjects[b] && sortedObjects[b].utm) || {};
    let s = 0, ch = 0, chC = 0;
    for (const ph of phrases) {
      if (!Object.prototype.hasOwnProperty.call(utm, ph)) continue;
      const u = utm[ph] || {};
      s += num(u.ses); ch += num(u.ch); chC += num(u.ch_c);
    }
    if (s > ses) ses = s;
    return { ch, chC, s };
  }).map(c => ({ ch: c.ch, chC: c.chC, chP: share(c.ch, c.s), chPc: share(c.chC, c.s) }));
  let conv = 0;
  for (const ph of phrases) {
    if (convByPhrase && Object.prototype.hasOwnProperty.call(convByPhrase, ph)) conv += num(convByPhrase[ph]);
  }
  return { ses, cells, conv, phrases };
}

export function median(values) {
  if (!values || !values.length) return 0;
  const s = values.slice().sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function quartiles(values) {
  if (!values || !values.length) return [0, 0];
  const s = values.slice().sort((a, b) => a - b);
  const at = p => {
    const i = (s.length - 1) * p;
    const lo = Math.floor(i), hi = Math.ceil(i);
    return s[lo] + (s[hi] - s[lo]) * (i - lo);
  };
  return [at(0.25), at(0.75)];
}

// Конверсии в разрезе слова: события по фразам (ответ /getUtmConversions)
// раскладываются по словам той же токенизацией, что и матрица. Одна сессия
// несёт одну фразу, поэтому конверсии по фразам складываются без двойного
// счёта, а splitPhraseToWords уже отдаёт уникальные слова — повтор слова
// внутри фразы её конверсии не удвоит.
export function conversionsByWord(convByPhrase) {
  const out = Object.create(null);
  for (const phrase in convByPhrase || {}) {
    if (!Object.prototype.hasOwnProperty.call(convByPhrase, phrase)) continue;
    const events = convByPhrase[phrase] || [];
    let form = 0;
    for (const e of events) if (e && e.form) form++;
    for (const word of splitPhraseToWords(phrase)) {
      if (!out[word]) out[word] = { all: 0, form: 0, micro: 0 };
      out[word].all += events.length;
      out[word].form += form;
      out[word].micro += events.length - form;
    }
  }
  return out;
}

// key: 'ses' | 'conv' | 'alpha' | 'total' | индекс блока (number)
// pick: (cell) => number — нужен для 'total' и числового ключа
export function sortWords(words, key, dir, pick) {
  const out = words.slice();
  if (key === 'alpha') {
    out.sort((a, b) => a.word.localeCompare(b.word, 'ru'));
    if (dir === 'asc') out.reverse();
    return out;
  }
  const get = key === 'ses' ? w => w.ses
    : key === 'conv' ? w => w.conv
    : key === 'total' ? w => (w.cells.length ? pick(w.cells[w.cells.length - 1]) : 0)
    : w => (w.cells[key] ? pick(w.cells[key]) : 0);
  // Направление переворачивает только первичный ключ. Алфавитный тай-брейк
  // всегда по возрастанию: иначе слова с равными значениями перетасовывались бы
  // при каждом щелчке по стрелке направления.
  const sign = dir === 'asc' ? -1 : 1;
  out.sort((a, b) => sign * (get(b) - get(a)) || a.word.localeCompare(b.word, 'ru'));
  return out;
}

export function filterByMinSessions(words, min) {
  const threshold = Number(min) || 0;
  if (threshold <= 0) return words;
  return words.filter(w => w.ses >= threshold);
}

export function cumBuckets() {
  return [0, 0.2, 0.4, 0.6, 0.8, 1];
}

// Проценты поблочного оттока малы: на шкале 0–100 % вся матрица окрасилась бы
// в один цвет. Поэтому для простого оттока шкала растягивается до максимума.
export function adaptiveBuckets(maxP) {
  const max = Number(maxP) || 0;
  if (max <= 0) return cumBuckets();
  // max * i / 5, а не (max / 5) * i: второй вариант копит ошибку двоичной
  // дроби и даёт 0.30000000000000004 вместо 0.3.
  return [0, 1, 2, 3, 4, 5].map(i => max * i / 5);
}

// 0 — отдельная корзина «нет оттока»; далее bounds[i-1] < p <= bounds[i].
// Дивергентная шкала отклонения от медианы блока (доли, не проценты): ±5 п.п.
// считаем «около медианы», дальше две ступени в каждую сторону. Возвращает
// шаг палитры --cell-N. Общая для матрицы и распределения (прототип 2054, 7821).
export function devStep(diff) {
  if (Math.abs(diff) <= 0.05) return 0;
  if (diff > 0.25) return 40;
  if (diff > 0.05) return 20;
  if (diff < -0.25) return 100;
  return 60;
}

export function bucketIndex(p, bounds) {
  if (!(p > 0)) return 0;
  for (let i = 1; i < bounds.length; i++) {
    if (p <= bounds[i] + 1e-12) return i;
  }
  return bounds.length - 1;
}

const pct = p => Math.round(p * 100) + ' %';

// Лента инсайтов: до шести отклонений, найденных по выбранной метрике.
// text — хвост фразы без самого слова, слово компонент выводит отдельно
// жирным. Это позволяет обойтись без v-html.
// meds и finalQuarts приходят снаружи и посчитаны по полному набору слов:
// порог «мин. сессий со словом» убирает слова из ленты, но не должен смещать
// саму статистику — иначе «выше медианы» в ленте означало бы не то же самое,
// что строка «медиана» в матрице.
export function computeInsights(matrix, pick, meds, finalQuarts) {
  const words = (matrix && matrix.words) || [];
  const blocks = (matrix && matrix.blocks) || [];
  if (!words.length || !blocks.length) return [];

  const val = (w, bi) => (w.cells[bi] ? pick(w.cells[bi]) : 0);
  const out = [];

  // 1. Максимальный скачок метрики за один блок. На первом блоке базой служит
  //    ноль: для кумулятивного оттока это настоящая потеря, посетители уходят
  //    сразу. Формулировка там другая, иначе карточка читалась бы как
  //    противоречие соседней «отток не растёт».
  let jump = { d: 0, wi: -1, bi: 0 };
  words.forEach((w, wi) => {
    for (let bi = 0; bi < blocks.length; bi++) {
      const d = val(w, bi) - (bi > 0 ? val(w, bi - 1) : 0);
      if (d > jump.d) jump = { d: d, wi: wi, bi: bi };
    }
  });
  if (jump.wi >= 0 && jump.d > 0) {
    out.push({ word: words[jump.wi].word, wi: jump.wi, bi: jump.bi,
      text: jump.bi === 0
        ? `— ${pct(jump.d)} оттока уже на «${blocks[0]}», максимальный скачок в срезе`
        : `— скачок +${pct(jump.d)} на «${blocks[jump.bi]}», максимальный в срезе` });
  }

  // Самая длинная серия блоков по одну сторону от медианы.
  const longestStreak = (w, side) => {
    let cur = 0, best = 0;
    for (let bi = 0; bi < blocks.length; bi++) {
      const hit = side === 'above' ? val(w, bi) > meds[bi] : val(w, bi) < meds[bi];
      if (hit) { cur++; if (cur > best) best = cur; } else cur = 0;
    }
    return best;
  };
  const topStreak = side => {
    let top = { len: 0, wi: -1 };
    words.forEach((w, wi) => {
      const len = longestStreak(w, side);
      if (len > top.len) top = { len: len, wi: wi };
    });
    return top;
  };

  // 2. Самая длинная серия блоков выше медианы.
  const worst = topStreak('above');
  if (worst.wi >= 0 && worst.len >= 3) {
    out.push({ word: words[worst.wi].word, wi: worst.wi, bi: null,
      text: `— отток выше медианы ${worst.len} блоков подряд` });
  }

  // 3. Зеркальное правило. Для оттока это не «плохо наоборот», а лучший
  //    результат в срезе — слово, которое стабильно теряет меньше остальных.
  const best = topStreak('below');
  if (best.wi >= 0 && best.len >= 3) {
    out.push({ word: words[best.wi].word, wi: best.wi, bi: null,
      text: `— отток ниже медианы ${best.len} блоков подряд` });
  }

  // 4. Слово выше медианы во всех блоках сразу. Берём одно: на широком срезе
  //    таких бывает больше десятка, и они занимали всю ленту целиком.
  const always = words.findIndex(w => blocks.every((_, bi) => val(w, bi) > meds[bi]));
  if (always >= 0) {
    out.push({ word: words[always].word, wi: always, bi: null,
      text: `— отток выше медианы во всех блоках (${blocks.length})` });
  }

  // 5. Отток перестал расти: хвост из одинаковых значений длиной от двух блоков.
  words.forEach((w, wi) => {
    const last = val(w, blocks.length - 1);
    let bi = blocks.length - 1;
    while (bi > 0 && val(w, bi - 1) === last) bi--;
    if (blocks.length - 1 - bi >= 2) {
      out.push({ word: w.word, wi: wi, bi: blocks.length - 1,
        text: `— отток не растёт с «${blocks[bi]}» (${pct(last)})` });
    }
  });

  // 6. Статистические выбросы по итогу, правило 1.5 × IQR.
  const finals = words.map(w => val(w, blocks.length - 1));
  const [q1, q3] = finalQuarts;
  const iqr = q3 - q1;
  words.forEach((w, wi) => {
    const v = val(w, blocks.length - 1);
    if (v > q3 + 1.5 * iqr) {
      out.push({ word: w.word, wi: wi, bi: blocks.length - 1,
        text: `— выброс вверх по итогу (${pct(v)})` });
    } else if (v < q1 - 1.5 * iqr) {
      out.push({ word: w.word, wi: wi, bi: blocks.length - 1,
        text: `— выброс вниз по итогу (${pct(v)})` });
    }
  });

  // Одно слово — одна карточка. Правила намеренно не взаимоисключающие, и без
  // этой свёртки лидер по обороту забирал бы половину ленты, повторяясь
  // разными формулировками и вытесняя более редкие находки вроде выбросов.
  // Побеждает первая карточка: правила идут от самой конкретной к самой общей.
  const seen = new Set();
  const unique = out.filter(item => {
    if (seen.has(item.wi)) return false;
    seen.add(item.wi);
    return true;
  });

  // 7. Если ничего не нашлось — показываем лидера по итогу, чтобы лента
  //    не была пустой при включённом тумблере.
  if (!unique.length) {
    let top = 0;
    words.forEach((w, wi) => { if (finals[wi] > finals[top]) top = wi; });
    unique.push({ word: words[top].word, wi: top, bi: blocks.length - 1,
      text: `— наибольший отток по итогу (${pct(finals[top])})` });
  }

  return unique.slice(0, 6);
}
